import dayjs from 'dayjs';
import duration, { DurationUnitType } from 'dayjs/plugin/duration';
import { DurationUnit } from '../interfaces/date';
import { nounPluralFormatter } from './string/formatter';

dayjs.extend(duration);

export const TIME_UNITS = [
  {
    denominator: 1,
    range: 1000,
    precision: 3,
    format: {
      singular: 'milli second',
      plural: 'milli seconds',
      abbr: 'ms'
    }
  },
  {
    denominator: 1000,
    range: 60,
    precision: 3,
    format: {
      singular: 'second',
      plural: 'seconds',
      abbr: 'sec'
    }
  },
  {
    denominator: 60,
    range: 60,
    precision: 2,
    format: {
      singular: 'minute',
      plural: 'minutes',
      abbr: 'min'
    }
  },
  {
    denominator: 60,
    range: 24,
    precision: 2,
    format: {
      singular: 'hour',
      plural: 'hours',
      abbr: 'hr'
    }
  },
  {
    denominator: 24,
    range: 365,
    precision: 0,
    format: {
      singular: 'day',
      plural: 'days',
      abbr: 'd'
    }
  }
];

export function getDurationString(
  d: number,
  useAbbr = false,
  separateUnits = false,
  precision?: number
) {
  let i = 0;
  let unitValue = d;

  while (i < TIME_UNITS.length) {
    unitValue /= TIME_UNITS[i].denominator;

    if (unitValue < TIME_UNITS[i].range || i === TIME_UNITS.length - 1) {
      if (separateUnits) {
        const integerPart = Math.floor(unitValue);
        const decimalPart = unitValue - integerPart;
        const nextUnitValue = decimalPart * TIME_UNITS[i].denominator;
        const nextUnitIntegerPart = Math.floor(nextUnitValue);

        let formattedString = `${integerPart} ${getTimeUnitString(
          integerPart,
          TIME_UNITS[i].format,
          useAbbr
        )}`;

        if (nextUnitIntegerPart > 0 && i > 0) {
          formattedString += ` ${nextUnitIntegerPart} ${getTimeUnitString(
            nextUnitIntegerPart,
            TIME_UNITS[i - 1].format,
            useAbbr
          )}`;
        }

        return formattedString;
      }

      if (precision == null) {
        // eslint-disable-next-line no-param-reassign
        precision = TIME_UNITS[i].precision;
      }
      return `${unitValue.toFixed(precision)} ${getTimeUnitString(
        unitValue,
        TIME_UNITS[i].format,
        useAbbr
      )}`;
    }

    i += 1;
  }
  return '';
}

export function getTimeUnitString(
  value: number,
  format: { singular: string; plural: string; abbr: string },
  useAbbr = false
) {
  if (useAbbr) {
    return format.abbr;
  }
  return value <= 1 ? format.singular : format.plural;
}

export function createDuration(value: number, unit: DurationUnit) {
  const localUnitToDayJsUnit: { [key in DurationUnit]: DurationUnitType } = {
    [DurationUnit.DAYS]: 'day',
    [DurationUnit.HOURS]: 'hour',
    [DurationUnit.SECONDS]: 'second',
    [DurationUnit.MINUTES]: 'minute',
    [DurationUnit.MILLISECONDS]: 'millisecond'
  };

  return dayjs.duration(value, localUnitToDayJsUnit[unit] as DurationUnitType);
}

export function millisToHours(value: number) {
  return createDuration(value, DurationUnit.MILLISECONDS).as('hour');
}

export function hoursToMillis(value: number) {
  return createDuration(value, DurationUnit.HOURS).as('millisecond');
}

export function millisToMinutes(value: number) {
  return createDuration(value, DurationUnit.MILLISECONDS).as('minute');
}

export function minutesToMillis(value: number) {
  return createDuration(value, DurationUnit.MINUTES).as('millisecond');
}

export const convertMillisToNearestUnits = (millis: number) => {
  const millisInMinutes = millisToMinutes(millis);
  const millisInHours = millisToHours(millis);
  let convertedUnits = {
    formattedUnit: 'mins',
    value: parseInt(millisInMinutes.toString(), 10),
    unit: DurationUnit.MINUTES
  };
  if (millisInHours % 1 === 0) {
    convertedUnits = {
      formattedUnit: nounPluralFormatter('hour', {
        count: millisInHours
      }),
      value: parseInt(millisInHours.toString(), 10),
      unit: DurationUnit.HOURS
    };
  }

  if (millisInHours % 24 === 0) {
    convertedUnits = {
      unit: DurationUnit.DAYS,
      formattedUnit: 'days',
      value: parseInt(createDuration(millis, DurationUnit.MILLISECONDS).as('day').toString(), 10)
    };
  }

  return convertedUnits;
};
